import React, { useState, useEffect, useRef } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import "./ContactSection.css";

// Helper function to encode form data for Netlify
const encode = (data) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
};

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
    honeypot: "",
  });
  const [loading, setLoading] = useState(false);
  const contactRef = useRef(null);

  useEffect(() => {
    const element = contactRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-in");
          }
        });
      },
      { threshold: 0.2 }
    );

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check honeypot for basic bot protection
    if (formData.honeypot) return;

    setLoading(true);

    // Submit to Netlify
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "contact", ...formData }),
    })
      .then(() => {
        toast.success("Message sent successfully! We will get back to you within 1 hour.");
        setFormData({ name: "", email: "", phone: "", service: "", message: "", honeypot: "" });
      })
      .catch((error) => {
        console.error("Form submission error:", error);
        toast.error("Something went wrong. Please try again later.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <section id="contact" className="contact-section" ref={contactRef} aria-labelledby="contact-title">
      <div className="contact-container">
        <div className="contact-content">
          <div className="contact-info">
            <span className="contact-label">GET IN TOUCH</span>
            <h2 className="contact-title" id="contact-title">Let's Talk</h2>
            <p className="contact-text">
              Ready to create something extraordinary? Let's start a
              conversation about how Oddtive can transform your brand's visual
              presence.
            </p>
            <div className="contact-details">
              <div className="detail-item">
                <h3 className="detail-title">Global Presence</h3>
                <p className="detail-text">
                  We work with clients worldwide, delivering world-class
                  production from anywhere.
                </p>
              </div>
              <div className="detail-item">
                <h3 className="detail-title">Response Time</h3>
                <p className="detail-text">
                  Expect a response within 1 hour. Your vision can't wait.
                </p>
              </div>
            </div>
          </div>

          {/* Added name, data-netlify, and honeypot attributes to the form tag */}
          <form 
            className="contact-form" 
            onSubmit={handleSubmit}
            name="contact"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
          >
            {/* Netlify requires this hidden input for React apps */}
            <input type="hidden" name="form-name" value="contact" />
            
            {/* Honeypot field - hidden from users */}
            <p className="hidden" style={{ display: 'none' }}>
              <label>
                Don’t fill this out if you're human:{" "}
                <input name="bot-field" onChange={handleChange} />
              </label>
            </p>

            <div className="form-group">
              <label htmlFor="name" className="form-label">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="Your name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="your@company.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone" className="form-label">Phone Number</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="form-input"
                placeholder="+92 (333) 000-0000"
              />
            </div>

            <div className="form-group">
              <label htmlFor="service" className="form-label">Service *</label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="form-input"
              >
                <option value="">Select a service</option>
                <option value="Single Ad">Single Ad</option>
                <option value="Social Media Services">Social Media Services</option>
                <option value="DVC, TVC or SF">DVC, TVC, SF</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="message" className="form-label">Message *</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="5"
                className="form-textarea"
                placeholder="Tell us about your project..."
              />
            </div>

            <button
              type="submit"
              className="btn-submit"
              disabled={loading}
            >
              {loading ? "Sending..." : "Send Message"}
              {!loading && <Send size={18} />}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;